import { useEffect, useState } from "react";
import { Minus, Plus, Package, ArrowDown, ArrowUp } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

export default function DriverInventory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLoad, setShowLoad] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/driver/inventory");
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const transfer = async (sku, direction) => {
    try {
      await api.post("/driver/inventory/transfer", { sku, qty: 1, direction });
      toast.success(`${direction === "load" ? "Loaded" : "Unloaded"} 1 × ${sku}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transfer failed.");
    }
  };

  return (
    <div className="space-y-5" data-testid="driver-inventory">
      <div className="flex items-end justify-between">
        <div>
          <span className="label-overline text-jow-gold">On the rikshaw</span>
          <h2 className="font-display text-3xl text-jow-navy mt-1">
            {items.reduce((a, b) => a + b.qty, 0)} pieces
          </h2>
        </div>
        <button
          onClick={() => setShowLoad((v) => !v)}
          className="btn-primary-jow text-[10px]"
          data-testid="inv-toggle-load"
        >
          <Plus size={13} /> Load from warehouse
        </button>
      </div>

      {showLoad && <LoadForm onSaved={() => { setShowLoad(false); load(); }} />}

      {loading && <p className="label-overline text-jow-navy/55">Loading…</p>}
      {!loading && items.length === 0 && (
        <div className="bg-white border border-jow-navy/10 p-10 text-center" data-testid="inv-empty">
          <Package size={26} className="text-jow-gold mx-auto" />
          <p className="mt-3 font-display text-xl text-jow-navy">Rikshaw is empty.</p>
          <p className="text-xs text-jow-navy/55 mt-1">Load some pieces from the warehouse.</p>
        </div>
      )}

      <ul className="space-y-3" data-testid="inv-list">
        {items.map((it) => (
          <li
            key={it.sku}
            className="bg-white border border-jow-navy/10 p-3 flex items-center gap-3"
            data-testid={`inv-row-${it.sku}`}
          >
            <img src={it.image} alt={it.name} className="w-14 h-14 object-cover" />
            <div className="flex-1 min-w-0">
              <p className="font-display text-sm text-jow-navy truncate">{it.name}</p>
              <p className="text-[10px] text-jow-navy/55 uppercase tracking-widest">
                {it.sku} · {it.attrs?.color}
              </p>
              <p className="text-xs text-jow-navy/70">₹{it.price_inr.toLocaleString("en-IN")}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => transfer(it.sku, "unload")}
                className="w-8 h-8 flex items-center justify-center border border-jow-navy/20 hover:bg-jow-navy hover:text-jow-cream"
                title="Unload (return to warehouse)"
                data-testid={`unload-${it.sku}`}
              >
                <ArrowUp size={14} />
              </button>
              <span className="font-display text-xl min-w-[2ch] text-center" data-testid={`qty-${it.sku}`}>
                {it.qty}
              </span>
              <button
                onClick={() => transfer(it.sku, "load")}
                className="w-8 h-8 flex items-center justify-center border border-jow-navy/20 hover:bg-jow-navy hover:text-jow-cream"
                title="Load one more from warehouse"
                data-testid={`load-${it.sku}`}
              >
                <ArrowDown size={14} />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LoadForm({ onSaved }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [qty, setQty] = useState({});

  useEffect(() => {
    api
      .get(`/products?q=${encodeURIComponent(q)}&limit=8`)
      .then((r) => setResults(r.data.items || []));
  }, [q]);

  const load = async (slug) => {
    try {
      const { data } = await api.get(`/products/${slug}`);
      const v = (data.variants || []).find((x) => (x.stock_warehouse || 0) > 0);
      if (!v) {
        toast.error("No warehouse stock for this piece.");
        return;
      }
      const n = Math.max(1, Number(qty[slug] || 1));
      await api.post("/driver/inventory/transfer", {
        sku: v.sku,
        qty: n,
        direction: "load",
      });
      toast.success(`Loaded ${n} × ${v.sku}`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed.");
    }
  };

  return (
    <div className="bg-jow-cream border border-jow-gold/40 p-4" data-testid="inv-load-form">
      <input
        type="text"
        placeholder="Search warehouse…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="input-jow border-b w-full"
        data-testid="inv-search"
      />
      <ul className="mt-3 divide-y divide-jow-navy/10">
        {results.slice(0, 6).map((p) => (
          <li key={p.slug} className="py-2 flex items-center gap-3">
            <img src={p.image} alt="" className="w-10 h-10 object-cover" />
            <p className="flex-1 text-sm text-jow-navy truncate">{p.name}</p>
            <input
              type="number"
              min="1"
              defaultValue={1}
              onChange={(e) => setQty({ ...qty, [p.slug]: e.target.value })}
              className="w-16 border border-jow-navy/15 px-2 py-1 text-sm"
            />
            <button
              onClick={() => load(p.slug)}
              className="text-xs uppercase tracking-widest text-jow-pink"
              data-testid={`load-product-${p.slug}`}
            >
              Load
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
